import { useEffect, useState } from 'react';

import { PIPELINE_DEFINITIONS_PAGE_SIZE } from 'src/components/pipelines/constants';
import { fetchPipelineDefinitions } from 'src/components/pipelines/hooks/use-pipeline-definitions';
import { Repository } from 'src/components/pipelines/models';
import { MergeQueue } from 'src/components/pipelines/models/MergeQueue';

export type UseMergeQueuePipelineDefinitionsResult = {
  loading: boolean;
  isPipelineDefinedForMergeQueue: boolean;
};

export function useMergeQueuePipelineDefinitions(
  repository: Repository | undefined,
  mergeQueue: MergeQueue | undefined
): UseMergeQueuePipelineDefinitionsResult {
  const shouldFetch =
    !!mergeQueue?.branch?.name && !!mergeQueue?.name && !!repository?.path;
  const [loading, setLoading] = useState(shouldFetch);
  const [isPipelineDefinedForMergeQueue, setIsPipelineDefinedForMergeQueue] =
    useState(!mergeQueue);

  useEffect(
    function checkIfPipelineDefinedForMergeQueue() {
      if (!mergeQueue) {
        setLoading(false);
        setIsPipelineDefinedForMergeQueue(true);
        return undefined;
      }

      if (!mergeQueue.branch?.name || !mergeQueue.name || !repository?.path) {
        setLoading(false);
        setIsPipelineDefinedForMergeQueue(false);
        return undefined;
      }

      const revision = mergeQueue.branch.name;
      const pattern = mergeQueue.name;

      let cancelled = false;
      setLoading(true);

      (async () => {
        try {
          const { size, error } = await fetchPipelineDefinitions(
            repository,
            revision,
            {
              page: 1,
              pagelen: PIPELINE_DEFINITIONS_PAGE_SIZE,
              section: 'merge-queues',
              pattern,
            }
          );
          if (cancelled) {
            return;
          }
          if (error) {
            setIsPipelineDefinedForMergeQueue(false);
          } else {
            setIsPipelineDefinedForMergeQueue(size > 0);
          }
        } catch {
          if (!cancelled) {
            setIsPipelineDefinedForMergeQueue(false);
          }
        } finally {
          if (!cancelled) {
            setLoading(false);
          }
        }
      })();

      return () => {
        cancelled = true;
      };
    },
    [mergeQueue, repository]
  );

  return { loading, isPipelineDefinedForMergeQueue };
}
